import { useState, useCallback, useRef } from "react";
import { useAuthenticatedFetchWithWorkspace } from "./Keycloak/useAuthenticatedFetchWithWorkspace";

export type QueryParams = Record<string, string | number | undefined | null>;

interface UseApiQueryOptions<T> {
    /** Transforme la réponse brute avant de la stocker. Stable via ref — pas besoin de mémoïser. */
    transform?: (raw: unknown) => T;
    /** Valeur initiale du state data. */
    initialData?: T;
    /** Message d'erreur fallback (défaut : "Une erreur est survenue"). */
    defaultError?: string;
    /** Injecte automatiquement workspace= dans les query params (défaut : true). */
    withWorkspace?: boolean;
}

/**
 * Hook générique pour les requêtes GET déclenchées à la demande.
 *
 * @example
 * const { data: history, isLoading, error, fetch: getHistory, reset } =
 *   useApiQuery<HistoryEntry[]>('/getPersonHistory', { withWorkspace: false, initialData: [] });
 *
 * // Dans un handler :
 * await getHistory({ person_id: personId });
 */
export function useApiQuery<T = unknown>(endpoint: string, options: UseApiQueryOptions<T> = {}) {
    const { initialData, defaultError = "Une erreur est survenue", withWorkspace = true } = options;
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    // Stable ref — évite de recréer `fetch` si transform change (ex: traduit avec useLanguage)
    const transformRef = useRef(options.transform);
    transformRef.current = options.transform;

    const [data, setData] = useState<T>(initialData as T);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const fetch = useCallback(
        async (params: QueryParams = {}): Promise<void> => {
            if (!isReady) return;
            const allParams: QueryParams = {
                ...(withWorkspace && workspaceId ? { workspace: workspaceId } : {}),
                ...params,
            };
            const qs = Object.entries(allParams)
                .filter(([, v]) => v !== undefined && v !== null)
                .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
                .join("&");

            setIsLoading(true);
            setError("");
            try {
                const raw = await authFetch(`${endpoint}${qs ? `?${qs}` : ""}`, { method: "GET" });
                setData(transformRef.current ? transformRef.current(raw) : (raw as T));
            } catch (err) {
                setError(err instanceof Error ? err.message : defaultError);
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady, workspaceId, endpoint, withWorkspace, defaultError]
    );

    const reset = useCallback(() => {
        setData(initialData as T);
        setError("");
    }, [initialData]);

    return { data, isLoading, error, fetch, reset };
}

/**
 * Normalise les réponses API qui peuvent être un tableau direct, { list: [...] } ou { data: [...] }.
 * Retourne [] si la réponse est vide / false / inattendue.
 */
export function toList<T>(raw: unknown): T[] {
    if (!raw || raw === false) return [];
    if (Array.isArray(raw)) return raw as T[];
    if (typeof raw === "object") {
        const r = raw as Record<string, unknown>;
        return (r.list ?? r.data ?? []) as T[];
    }
    return [];
}
